import { useState, useRef, useCallback } from 'react';
import documentService from '../services/documentService';
import useNotification from './useNotification';

/**
 * Custom hook for document-related operations
 */
// Debounce function to limit API calls
const debounce = (func, wait) => {
  let timeout;
  
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const useDocuments = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [document, setDocument] = useState(null);
  const { showNotification } = useNotification();
  
  // Store the last fetch time for each project to reduce API calls
  const lastFetchRef = useRef({});
  const documentsCache = useRef({});

  /**
   * Get all documents for a project
   * @param {string} projectId - The project ID
   */
  const getProjectDocuments = useCallback(async (projectId) => {
    // If we have a recent fetch (within last 5 seconds), return cached data
    const now = Date.now();
    const lastFetch = lastFetchRef.current[projectId] || 0;
    
    // If we have cached data and it's less than 5 seconds old, return it
    if (documentsCache.current[projectId] && (now - lastFetch) < 5000) {
      console.log('Using cached documents for project', projectId);
      return documentsCache.current[projectId];
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching documents for project', projectId);
      const data = await documentService.getProjectDocuments(projectId);
      
      // Update cache and last fetch time, but don't update state that would cause re-renders
      // Only update local state if needed
      if (JSON.stringify(data) !== JSON.stringify(documentsCache.current[projectId])) {
        setDocuments(data);
      }
      
      documentsCache.current[projectId] = data;
      lastFetchRef.current[projectId] = now;
      
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch project documents');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get a document by ID
   * @param {string} documentId - The document ID
   */
  const getDocumentById = async (documentId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.getDocumentById(documentId);
      setDocument(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to fetch document');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate APD document from questionnaire responses
   * @param {string} projectId - The project ID
   */
  const generateAPD = async (projectId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.generateAPD(projectId);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to generate APD document');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Update document content
   * @param {string} documentId - The document ID
   * @param {string} content - The new document content
   */
  const updateDocument = async (documentId, content) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.updateDocument(documentId, content);
      setDocument(data);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to update document');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Extract PRD content from a project requirement document
   * @param {string} projectId - The project ID
   * @param {string} content - The content to extract PRD from
   */
  const extractPRD = async (projectId, content) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.extractPRD(projectId, content);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to extract PRD');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate field validations based on PRD
   * @param {string} documentId - The PRD document ID
   * @param {Object} instructions - Optional validation instructions
   */
  const generateFieldValidations = async (documentId, instructions) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.generateFieldValidations(documentId, instructions);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to generate field validations');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate technology stack guidelines
   * @param {string} documentId - The PRD document ID
   */
  const generateTechStack = async (documentId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.generateTechStack(documentId);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to generate technology stack');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate development guidelines
   * @param {string} documentId - The PRD document ID
   * @param {Object} techStack - The technology stack info
   */
  const generateDevGuidelines = async (documentId, techStack) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.generateDevGuidelines(documentId, techStack);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to generate development guidelines');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate design guidelines
   * @param {string} documentId - The PRD document ID
   * @param {Object} designInfo - Optional design information (Figma URL, etc.)
   */
  const generateDesignGuidelines = async (documentId, designInfo) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.generateDesignGuidelines(documentId, designInfo);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to generate design guidelines');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate security guidelines following OWASP
   * @param {string} documentId - The PRD document ID
   */
  const generateSecurityGuidelines = async (documentId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.generateSecurityGuidelines(documentId);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to generate security guidelines');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate API guidelines
   * @param {string} documentId - The PRD document ID
   */
  const generateAPIGuidelines = async (documentId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.generateAPIGuidelines(documentId);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to generate API guidelines');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate features list from PRD
   * @param {string} documentId - The PRD document ID
   */
  const generateFeatures = async (documentId) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.generateFeatures(documentId);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to generate features');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate user stories from PRD and features
   * @param {string} documentId - The PRD document ID
   * @param {Array} features - The features list
   */
  const generateUserStories = async (documentId, features) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.generateUserStories(documentId, features);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to generate user stories');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Generate final PRD document after all steps
   * @param {string} projectId - The project ID
   * @param {Object} documents - All generated documents
   */
  const generateFinalPRD = async (projectId, documents) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.generateFinalPRD(projectId, documents);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to generate final PRD');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Connect to Claude Code terminal to start development
   * @param {string} projectId - The project ID
   * @param {Array} documents - All documents needed for development
   */
  const connectToClaudeCode = async (projectId, documents) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.connectToClaudeCode(projectId, documents);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to connect to Claude Code');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Setup GitLab repository and commit code
   * @param {string} projectId - The project ID
   * @param {Object} gitConfig - Git configuration (repo name, etc.)
   */
  const setupGitRepository = async (projectId, gitConfig) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.setupGitRepository(projectId, gitConfig);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to setup git repository');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Deploy the application
   * @param {string} projectId - The project ID
   * @param {Object} deployConfig - Deployment configuration
   */
  const deployApplication = async (projectId, deployConfig) => {
    try {
      setLoading(true);
      setError(null);
      const data = await documentService.deployApplication(projectId, deployConfig);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to deploy application');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Track progress of the PRD workflow
   * @param {string} projectId - The project ID
   */
  const trackPRDProgress = async (projectId) => {
    // Use a static flag to prevent multiple simultaneous calls
    if (trackPRDProgress.isLoading) {
      console.log('Preventing duplicate trackPRDProgress call');
      return null;
    }
    
    try {
      trackPRDProgress.isLoading = true;
      setLoading(true);
      setError(null);
      
      console.log('Making trackPRDProgress call for project:', projectId);
      const data = await documentService.trackPRDProgress(projectId);
      console.log('Progress data from API:', data);
      return data;
    } catch (err) {
      console.error('Error in trackPRDProgress:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to track PRD progress';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
      // Reset the loading flag after a delay to prevent rapid successive calls
      setTimeout(() => {
        trackPRDProgress.isLoading = false;
      }, 1000);
    }
  };
  
  // Initialize the static flag
  trackPRDProgress.isLoading = false;

  /**
   * Create a standalone document from content
   * @param {string} projectId - The project ID
   * @param {Object} documentData - Document data with title, content, and type
   */
  const createDocument = async (projectId, documentData) => {
    try {
      setLoading(true);
      setError(null);
      
      // Ensure we have a taskId and sectionId if possible
      let enhancedDocumentData = { ...documentData };
      
      // Import document mapping utility to enhance the document data
      try {
        const documentMapping = require('../utils/documentMapping');
        enhancedDocumentData = documentMapping.getStandardizedDocumentData(documentData);
      } catch (mappingError) {
        console.warn('Could not enhance document data with mappings:', mappingError);
      }
      
      const data = await documentService.createDocument(projectId, enhancedDocumentData);
      return data;
    } catch (err) {
      setError(err.message || 'Failed to create document');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Save markdown file to project
   * @param {string} projectId - The project ID
   * @param {string} filename - The markdown filename
   * @param {string} content - The markdown content
   * @param {string} taskId - Optional task ID for the document
   * @param {string} sectionId - Optional section ID for the document
   */
  const saveMdFile = async (projectId, filename, content, taskId, sectionId) => {
    try {
      setLoading(true);
      setError(null);
      
      // Determine taskId from filename if not provided
      let finalTaskId = taskId;
      let finalSectionId = sectionId;
      
      if (!finalTaskId || !finalSectionId) {
        try {
          // Derive the taskId from the filename if possible
          const { getSectionIdForTask } = require('../utils/documentMapping');
          
          // Extract potential taskId from filename (e.g., "tech-stack.md" → "tech-stack")
          if (!finalTaskId) {
            const filenameBase = filename.split('.')[0];
            if (getSectionIdForTask(filenameBase)) {
              finalTaskId = filenameBase;
            }
          }
          
          // Get sectionId from taskId if available
          if (finalTaskId && !finalSectionId) {
            finalSectionId = getSectionIdForTask(finalTaskId);
          }
        } catch (err) {
          console.warn('Could not determine task/section from filename:', err);
        }
      }
      
      const data = await documentService.saveMdFile(
        projectId, 
        filename, 
        content, 
        finalTaskId, 
        finalSectionId
      );
      return data;
    } catch (err) {
      setError(err.message || 'Failed to save markdown file');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    documents,
    document,
    getProjectDocuments,
    getDocumentById,
    generateAPD,
    updateDocument,
    extractPRD,
    generateFieldValidations,
    generateTechStack,
    generateDevGuidelines,
    generateDesignGuidelines,
    generateSecurityGuidelines,
    generateAPIGuidelines,
    generateFeatures,
    generateUserStories,
    generateFinalPRD,
    connectToClaudeCode,
    setupGitRepository,
    deployApplication,
    trackPRDProgress,
    createDocument,
    saveMdFile
  };
};

export default useDocuments;